import { useMyDocuments } from '@/api/useMyDocuments';
import {
  formatProjectStatus,
  formatSalesStage,
  formatUnlockStatus,
  useMyOrders,
  type MeOrderDto,
  type MeOrderProjectDto,
} from '@/api/useMyOrders';
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageHeader,
  StageBadge,
} from '@/components/AppShell';
import { formatDateTime } from '@/utils/formatDate';
import { formatMoneyCNY } from '@/utils/money';

/**
 * 账单 / 订单只读概览（Issue #198 → Issue #214）。
 *
 * 数据来源：
 *   - GET /api/v1/me/orders 返回当前客户的订单、服务用户、项目、付款汇总、
 *     项目解锁状态与下一步提示。身份由后端从 access token 派生，客户端
 *     不传 customerId / tenantId / orgId。
 *   - 资料列表仍来自 /api/v1/me/documents，用于补充展示与订单相关的资料
 *     审核进度（身份证、合同等）。
 *
 * 红线：
 *   - 金额、付款比例、解锁阈值都是后端下发的 decimal 字符串；UI 不做
 *     number 加减。
 *   - 不展示 tenantId / orgId / costPrice / 内部 operator ID 等敏感字段。
 *   - 错误状态严格走 401 → refresh → 提示重新登录；404 = 客户档案尚未建立。
 */
export function FinancePage() {
  const orders = useMyOrders();
  const documents = useMyDocuments();
  const financialDocs = documents.documents.filter(
    (doc) => doc.docKind === 'identity' || doc.docKind === 'document',
  );

  return (
    <div>
      <PageHeader
        title="账单状态"
        description="查看您的订单、付款进度与项目解锁状态。"
      />
      {orders.error ? (
        <ErrorBanner message={orders.error.message} onRetry={orders.refetch} />
      ) : null}
      {documents.error ? (
        <ErrorBanner
          message={documents.error.message}
          onRetry={documents.refetch}
        />
      ) : null}

      <section aria-label="订单与付款" className="mb-6">
        {orders.loading ? (
          <Card>
            <p className="text-sm text-muted-foreground">订单加载中…</p>
          </Card>
        ) : orders.notFound ? (
          <Card>
            <EmptyState
              title="账户尚未关联客户档案"
              description="如已与顾问完成签约，请联系顾问确认账户绑定后再查看账单。"
            />
          </Card>
        ) : !orders.data || orders.data.orders.length === 0 ? (
          <Card>
            <EmptyState
              title="暂无订单记录"
              description="完成签约后，订单、付款与项目解锁状态会自动出现在这里。"
            />
          </Card>
        ) : (
          <ul className="space-y-3">
            {orders.data.orders.map((order) => (
              <li key={order.id}>
                <OrderCard order={order} />
              </li>
            ))}
          </ul>
        )}
      </section>

      <h2 className="mb-2 text-sm font-semibold">与订单相关的资料</h2>
      {documents.loading ? (
        <Card>
          <p className="text-sm text-muted-foreground">资料加载中…</p>
        </Card>
      ) : financialDocs.length === 0 ? (
        <Card>
          <EmptyState
            title="暂无订单相关资料"
            description="如顾问请您上传身份证明或合同附件，请在“资料上传”页面提交。"
          />
        </Card>
      ) : (
        <ul className="space-y-3">
          {financialDocs.map((doc) => (
            <li key={doc.id}>
              <Card className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <p className="truncate text-sm font-medium">{doc.title}</p>
                  <FinanceDocBadge state={doc.reviewState} />
                </div>
                <p className="text-xs text-muted-foreground">
                  上传时间 {formatDateTime(doc.createdAt)}
                </p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrderCard({ order }: { order: MeOrderDto }) {
  return (
    <Card className="space-y-3">
      <header className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium">{order.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            订单号 {order.orderNo}
          </p>
        </div>
        <StageBadge label={formatSalesStage(order.salesStage)} tone="progress" />
      </header>

      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-muted-foreground">订单金额</dt>
          <dd className="mt-0.5 font-medium">
            {formatMoneyCNY(order.totalAmount ?? null)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">创建时间</dt>
          <dd className="mt-0.5">{formatDateTime(order.createdAt)}</dd>
        </div>
      </dl>

      {order.serviceUsers.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          服务对象：
          {order.serviceUsers.map((u) => u.name).join('、')}
        </p>
      ) : null}

      {order.projects.length > 0 ? (
        <ul className="space-y-2">
          {order.projects.map((project) => (
            <li key={project.id}>
              <ProjectRow project={project} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">本订单暂未添加项目明细。</p>
      )}

      {order.nextStepHint ? (
        <p className="rounded-md bg-muted px-3 py-2 text-xs">
          下一步：{order.nextStepHint}
        </p>
      ) : null}
    </Card>
  );
}

function ProjectRow({ project }: { project: MeOrderProjectDto }) {
  const unlock = formatUnlockStatus(project.unlock);
  return (
    <div className="rounded-md border border-border/60 bg-card/50 p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{project.projectName}</p>
          {project.projectCode ? (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {project.projectCode}
            </p>
          ) : null}
        </div>
        <StageBadge label={formatProjectStatus(project.status)} tone="neutral" />
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-muted-foreground">项目金额</dt>
          <dd className="mt-0.5 font-medium">
            {formatMoneyCNY(project.totalAmount)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">已确认付款</dt>
          <dd className="mt-0.5 font-medium">
            {formatMoneyCNY(project.paymentSummary.totalConfirmedAmount)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">付款进度</dt>
          <dd className="mt-0.5">
            {formatPercentDecimal(project.paymentSummary.paidRatio)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">解锁阈值</dt>
          <dd className="mt-0.5">
            {formatPercentDecimal(project.paymentSummary.unlockRatio)}
          </dd>
        </div>
      </dl>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-muted-foreground">项目解锁</span>
        <StageBadge label={unlock.label} tone={unlock.tone} />
      </div>
      {project.paymentSummary.lastConfirmedPaidAt ? (
        <p className="mt-1 text-[11px] text-muted-foreground">
          最近付款 {formatDateTime(project.paymentSummary.lastConfirmedPaidAt)}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Format a 0..1 decimal-string ratio as a percentage string for display.
 * Returns "—" for invalid inputs so the UI never crashes on bad data.
 */
export function formatPercentDecimal(value: string): string {
  if (!value) return '—';
  const trimmed = value.trim();
  if (trimmed.length === 0) return '—';
  if (!/^-?\d+(?:\.\d+)?$/.test(trimmed)) return '—';
  const sign = trimmed.startsWith('-') ? '-' : '';
  const body = sign ? trimmed.slice(1) : trimmed;
  const [intPart, fracPart = ''] = body.split('.');
  // Multiply by 100 by shifting the decimal point two places to the right.
  const fracPadded = fracPart.padEnd(2, '0');
  const shiftedIntStr = intPart + fracPadded.slice(0, 2);
  const shiftedFrac = fracPadded.slice(2);
  const shiftedInt = shiftedIntStr.replace(/^0+(?=\d)/, '');
  const trimmedFrac = shiftedFrac.replace(/0+$/, '');
  const display =
    trimmedFrac.length > 0
      ? `${shiftedInt}.${trimmedFrac.slice(0, 2)}`
      : shiftedInt;
  return `${sign}${display}%`;
}

function FinanceDocBadge({
  state,
}: {
  state: 'awaiting_upload' | 'pending_review' | 'approved' | 'rejected' | 'published' | 'retracted';
}) {
  switch (state) {
    case 'awaiting_upload':
      return <StageBadge label="待上传" tone="neutral" />;
    case 'pending_review':
      return <StageBadge label="审核中" tone="progress" />;
    case 'approved':
    case 'published':
      return <StageBadge label="已通过" tone="good" />;
    case 'rejected':
      return <StageBadge label="未通过" tone="warn" />;
    case 'retracted':
      return <StageBadge label="已撤回" tone="warn" />;
    default:
      return <StageBadge label={state} tone="neutral" />;
  }
}
